import { FormattedMessage } from "react-intl";
import { Column } from "react-table";
import notPermittedIcon from "../../../../assets/management/permission_no.svg";
import permittedIcon from "../../../../assets/management/permission_yes.svg";
import superAdminUserIcon from "../../../../assets/users/super_admin.svg";
import adminUserIcon from "../../../../assets/users/admin.svg";
import proUserIcon from "../../../../assets/users/professional_user.svg";
import Role from "../../../../models/role";
import ActiveIndicator from "../../../Shared/ActiveIndicator/ActiveIndicator";
import DateTime from "../../../Shared/DateTime/DateTime";
import HighlightText from "../../../Shared/HighlightText/HighlightText";
import TableColumnHeader from "../../../Shared/Table/TableColumnHeader/TableColumnHeader";
import PendingUserIcon from "../../PendingUserIcon/PendingUserIcon";
import { UsersTableItem } from "./ManageUsersTable";
import styles from "./ManageUsersTable.module.css";

export const manageUsersTableColumns = (
  highlightText?: string
): Column<UsersTableItem>[] => [
  {
    accessor: "entity",
    Header: ({ column }) => (
      <TableColumnHeader
        sortable
        isSorted={column.isSorted}
        isSortedDesc={column.isSortedDesc}
      >
        <span className="capitalized">
          <FormattedMessage id="MANAGEMENT.ENTITY" defaultMessage="Entity" />
        </span>
      </TableColumnHeader>
    ),
    Cell: ({ row }) => (
      <HighlightText
        text={row.original?.entity ?? ""}
        highlightText={[highlightText ?? ""]}
      />
    ),
  },
  {
    accessor: "firstName",
    Header: ({ column }) => (
      <TableColumnHeader
        sortable
        isSorted={column.isSorted}
        isSortedDesc={column.isSortedDesc}
      >
        <span className="capitalized">
          <FormattedMessage id="USER.FIRST_NAME" defaultMessage="First Name" />
        </span>
      </TableColumnHeader>
    ),
    width: "8%",
    Cell: ({ row }) => (
      <HighlightText
        text={row.original?.firstName ?? ""}
        highlightText={[highlightText ?? ""]}
      />
    ),
  },
  {
    accessor: "lastName",
    Header: ({ column }) => (
      <TableColumnHeader
        sortable
        isSorted={column.isSorted}
        isSortedDesc={column.isSortedDesc}
      >
        <span className="capitalized">
          <FormattedMessage id="USER.LAST_NAME" defaultMessage="Last Name" />
        </span>
      </TableColumnHeader>
    ),
    width: "8%",
    Cell: ({ row }) => (
      <HighlightText
        text={row.original?.lastName ?? ""}
        highlightText={[highlightText ?? ""]}
      />
    ),
  },
  {
    accessor: "title",
    Header: ({ column }) => (
      <TableColumnHeader
        sortable
        isSorted={column.isSorted}
        isSortedDesc={column.isSortedDesc}
      >
        <span className="capitalized">
          <FormattedMessage id="USER.TITLE" defaultMessage="Title" />
        </span>
      </TableColumnHeader>
    ),
    Cell: ({ row }) => (
      <HighlightText
        text={row.original?.title ?? ""}
        highlightText={[highlightText ?? ""]}
      />
    ),
  },
  {
    accessor: "email",
    Header: ({ column }) => (
      <TableColumnHeader
        sortable
        isSorted={column.isSorted}
        isSortedDesc={column.isSortedDesc}
      >
        <span className="capitalized">
          <FormattedMessage id="USER.EMAIL" defaultMessage="Email" />
        </span>
      </TableColumnHeader>
    ),
    Cell: ({ row }) => (
      <HighlightText
        text={row.original?.email ?? ""}
        highlightText={[highlightText ?? ""]}
      />
    ),
    width: "5%",
  },
  {
    accessor: "phone",
    Header: ({ column }) => (
      <TableColumnHeader
        sortable
        isSorted={column.isSorted}
        isSortedDesc={column.isSortedDesc}
      >
        <span className="capitalized">
          <FormattedMessage id="USER.PHONE" defaultMessage="Phone" />
        </span>
      </TableColumnHeader>
    ),
    Cell: ({ row }) => (
      <HighlightText
        text={row.original?.phone ?? ""}
        highlightText={[highlightText ?? ""]}
      />
    ),
  },
  {
    accessor: "role",
    Header: ({ column }) => (
      <TableColumnHeader
        sortable
        isSorted={column.isSorted}
        isSortedDesc={column.isSortedDesc}
      >
        <span className="capitalized">
          <FormattedMessage id="USER.ROLE" defaultMessage="Role" />
        </span>
      </TableColumnHeader>
    ),
    Cell: ({ row }) => {
      const isAdmin =
        row.original.role === Role.OrgProfessional &&
        row.original.canManageProUsers;
      const isSuperAdmin = row.original?.role === Role.OrgAdmin;

      return (
        <div className="d-flex align-items-center">
          <img
            className={
              isSuperAdmin
                ? styles.superAdminIcon
                : isAdmin
                ? styles.adminIcon
                : styles.userIcon
            }
            src={
              isSuperAdmin
                ? superAdminUserIcon
                : isAdmin
                ? adminUserIcon
                : proUserIcon
            }
            alt=""
          />
          <>
            <span className="mx-1"></span>
            <span className="capitalized font-md">
              {isSuperAdmin || isAdmin ? (
                <FormattedMessage
                  id={
                    isSuperAdmin ? "MANAGEMENT.SUPERADMIN" : "MANAGEMENT.ADMIN"
                  }
                  defaultMessage={isSuperAdmin ? "Super Admin" : "ADMIN"}
                />
              ) : (
                ""
              )}
            </span>
          </>
        </div>
      );
    },
    width: "7%",
  },
  {
    accessor: "logInPortal",
    Header: ({ column }) => (
      <TableColumnHeader
        sortable
        isSorted={column.isSorted}
        isSortedDesc={column.isSortedDesc}
      >
        <span className="capitalized">
          <FormattedMessage
            id="USER.PERMISSIONS.LOG_INTO_PORTAL"
            defaultMessage="log Into Portal"
          />
        </span>
      </TableColumnHeader>
    ),
    Cell: ({ row }) => (
      <div className="text-center">
        <img
          className={`${styles.permissionIcon}`}
          src={row.original?.logInPortal ? permittedIcon : notPermittedIcon}
          alt=""
        />
      </div>
    ),
    width: "7%",
  },
  {
    accessor: "interpretEcg",
    Header: ({ column }) => (
      <TableColumnHeader
        sortable
        isSorted={column.isSorted}
        isSortedDesc={column.isSortedDesc}
      >
        <span className="capitalized">
          <FormattedMessage
            id="USER.PERMISSIONS.INTERPRET_ECG"
            defaultMessage="Interpret ECG"
          />
        </span>
      </TableColumnHeader>
    ),
    Cell: ({ row }) => (
      <div className="text-center">
        <img
          className={`${styles.permissionIcon}`}
          src={row.original?.interpretEcg ? permittedIcon : notPermittedIcon}
          alt=""
        />
      </div>
    ),
    width: "7%",
  },
  {
    accessor: "managePatientAccounts",
    Header: ({ column }) => (
      <TableColumnHeader
        sortable
        isSorted={column.isSorted}
        isSortedDesc={column.isSortedDesc}
      >
        <span className="capitalized">
          <FormattedMessage
            id="USER.PERMISSIONS.MANAGE_PATIENT_ACCOUNTS"
            defaultMessage="Manage Patient Accounts"
          />
        </span>
      </TableColumnHeader>
    ),
    Cell: ({ row }) => (
      <div className="text-center">
        <img
          className={`${styles.permissionIcon}`}
          src={
            row.original?.managePatientAccounts
              ? permittedIcon
              : notPermittedIcon
          }
          alt=""
        />
      </div>
    ),
    width: "11%",
  },
  {
    accessor: "creationDate",
    Header: ({ column }) => (
      <TableColumnHeader
        sortable
        isSorted={column.isSorted}
        isSortedDesc={column.isSortedDesc}
      >
        <span className="capitalized">
          <FormattedMessage
            id="USER.CREATION_DATE"
            defaultMessage="Creation Date"
          />
        </span>
      </TableColumnHeader>
    ),
    Cell: ({ row }) => <DateTime>{row.original?.creationDate}</DateTime>,
  },
  {
    accessor: "status",
    Header: ({ column }) => (
      <TableColumnHeader
        sortable
        isSorted={column.isSorted}
        isSortedDesc={column.isSortedDesc}
      >
        <span className="capitalized">
          <FormattedMessage id="USER.STATUS" defaultMessage="Status" />
        </span>
      </TableColumnHeader>
    ),
    Cell: ({ row }) => {
      switch (row.original?.status) {
        case "pending":
          return <PendingUserIcon />;
        default:
          return <ActiveIndicator active={row.original?.status === "active"} />;
      }
    },
    width: "5%",
  },
];
